import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
} from '@mui/material';
import {
  SmartToy as AgentIcon,
  Security as SecurityIcon,
  Business as CompanyIcon,
  Storage as DatabaseIcon,
  CloudSync as IntegrationIcon,
  Person as PersonIcon,
  AdminPanelSettings as RoleIcon,
  Notifications as NotificationsIcon,
  History as AuditIcon,
  VpnKey as ApiKeyIcon,
  AccountTree as WorkflowIcon,
  AutoFixHigh as AutomationIcon,
  Backup as BackupIcon,
} from '@mui/icons-material';
import AgentSettings from '../components/Settings/AgentSettings';
import UserManagement from '../components/Settings/UserManagement';
import RoleManagement from '../components/Settings/RoleManagement';
import SecurityPolicies from '../components/Settings/SecurityPolicies';
import CompanySettings from '../components/Settings/CompanySettings';
import DataManagement from '../components/Settings/DataManagement';
import BackupSettings from '../components/Settings/BackupSettings';
import SystemIntegrations from '../components/Settings/SystemIntegrations';
import NotificationSettings from '../components/Settings/NotificationSettings';
import AuditLog from '../components/Settings/AuditLog';
import ApiKeys from '../components/Settings/ApiKeys';
import Workflows from '../components/Settings/Workflows';
import AutomationRules from '../components/Settings/AutomationRules';
import { usePermission } from '../hooks/usePermission';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

const Settings = () => {
  const { hasPermission, canReadAgents } = usePermission();
  // Set by SystemIntegrations while it has unsaved changes so we can warn
  // before the tab switch unmounts it.
  const dirtyRef = useRef(false);

  const canReadCompany = hasPermission('company', 'read');
  const canManageCompany = hasPermission('company', 'manage');
  const canReadCases = hasPermission('cases', 'read');

  // Only tabs the user may read are rendered, so nobody hits a 403 by clicking.
  const tabs = useMemo(() => [
    { key: 'users', label: 'User Management', icon: <PersonIcon />, show: hasPermission('users', 'read'), render: () => <UserManagement /> },
    { key: 'roles', label: 'Role Management', icon: <RoleIcon />, show: hasPermission('roles', 'read'), render: () => <RoleManagement /> },
    { key: 'agents', label: 'Agent Integrations', icon: <AgentIcon />, show: canReadAgents, render: () => <AgentSettings /> },
    { key: 'security', label: 'Security Policies', icon: <SecurityIcon />, show: canReadCompany, render: () => <SecurityPolicies /> },
    { key: 'company', label: 'Company Settings', icon: <CompanyIcon />, show: canReadCompany, render: () => <CompanySettings /> },
    { key: 'data', label: 'Data Management', icon: <DatabaseIcon />, show: canReadCompany, render: () => <DataManagement /> },
    { key: 'backups', label: 'Backups', icon: <BackupIcon />, show: canManageCompany, render: () => <BackupSettings /> },
    {
      key: 'integrations', label: 'System Integrations', icon: <IntegrationIcon />, show: canReadCompany,
      render: () => <SystemIntegrations onDirtyChange={(dirty) => { dirtyRef.current = dirty; }} />
    },
    { key: 'notifications', label: 'Notifications', icon: <NotificationsIcon />, show: canReadCompany, render: () => <NotificationSettings /> },
    { key: 'workflows', label: 'Playbooks & SLA', icon: <WorkflowIcon />, show: canReadCases, render: () => <Workflows /> },
    { key: 'automation', label: 'Automation', icon: <AutomationIcon />, show: hasPermission('detections', 'read'), render: () => <AutomationRules /> },
    { key: 'apiKeys', label: 'API keys', icon: <ApiKeyIcon />, show: canManageCompany, render: () => <ApiKeys /> },
    { key: 'audit', label: 'Audit log', icon: <AuditIcon />, show: canManageCompany, render: () => <AuditLog /> },
  ].filter(tab => tab.show), [hasPermission, canReadAgents, canReadCompany, canManageCompany, canReadCases]);

  const [tabValue, setTabValue] = useState(0);
  const location = useLocation();

  // Deep links such as /dashboard/settings?tab=backups
  useEffect(() => {
    const wanted = new URLSearchParams(location.search).get('tab');
    if (!wanted) return;
    const index = tabs.findIndex((t) => t.key === wanted);
    if (index >= 0) setTabValue(index);
  }, [location.search, tabs]);

  // Keep the index valid if permissions change (e.g. role updated in another tab)
  useEffect(() => {
    if (tabValue >= tabs.length) setTabValue(0);
  }, [tabs.length, tabValue]);

  const handleTabChange = (event, newValue) => {
    if (dirtyRef.current) {
      const leave = window.confirm('You have unsaved integration changes. Leave this tab and discard them?');
      if (!leave) return;
      dirtyRef.current = false;
    }
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        System Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configure system-wide settings, integrations, and administrative preferences.
      </Typography>

      {tabs.length === 0 ? (
        <Alert severity="info" sx={{ mt: 3 }}>
          Your role does not include access to any settings areas. Contact a company administrator if you need
          access to user, role, or company configuration.
        </Alert>
      ) : (
        <Paper sx={{ width: '100%', mt: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={Math.min(tabValue, tabs.length - 1)}
              onChange={handleTabChange}
              aria-label="settings tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={tab.key}
                  icon={tab.icon}
                  label={tab.label}
                  {...a11yProps(index)}
                  sx={{ minHeight: 72 }}
                />
              ))}
            </Tabs>
          </Box>

          {tabs.map((tab, index) => (
            <TabPanel key={tab.key} value={Math.min(tabValue, tabs.length - 1)} index={index}>
              {tab.render()}
            </TabPanel>
          ))}
        </Paper>
      )}
    </Container>
  );
};

export default Settings;
